import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  TextField,
  Select,
  MenuItem,
  Alert,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloseIcon from "@mui/icons-material/Close";
import RepeatIcon from "@mui/icons-material/Repeat";
import EventIcon from "@mui/icons-material/Event";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// ── Constants ──────────────────────────────────────────────────────────────────

const CATEGORY_COLOR = {
  firefighter: "#C2410C",
  ems: "var(--color-primary)",
  hazmat: "#B5651D",
  rescue: "#15803D",
};

const CATEGORY_BG = {
  firefighter: "rgba(194,65,12,.10)",
  ems: "rgba(37,99,235,.10)",
  hazmat: "rgba(181,101,29,.10)",
  rescue: "rgba(21,128,61,.10)",
};

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const DURATION_OPTIONS = [
  { value: "1", label: "1 hour" },
  { value: "2", label: "2 hours" },
  { value: "3", label: "3 hours" },
  { value: "4", label: "4 hours" },
  { value: "6", label: "6 hours" },
  { value: "8", label: "8 hours" },
  { value: "16", label: "16 hours (2-day)" },
];

const QUICK_PATTERNS = [
  { label: "Tue & Thu · 6 wks", days: [2, 4], weeks: 6 },
  { label: "Mon/Wed/Fri · 4 wks", days: [1, 3, 5], weeks: 4 },
  { label: "Weekdays · 2 wks", days: [1, 2, 3, 4, 5], weeks: 2 },
  { label: "Saturdays · 8 wks", days: [6], weeks: 8 },
];

// ── Helpers ────────────────────────────────────────────────────────────────────

function parseLocalDate(dateStr) {
  if (!dateStr) return new Date();
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function toDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatShort(dateStr) {
  return parseLocalDate(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function addHours(timeStr, hours) {
  const [h, m] = (timeStr || "09:00").split(":").map(Number);
  const totalMins = h * 60 + m + hours * 60;
  const nh = Math.floor(totalMins / 60) % 24;
  const nm = totalMins % 60;
  return `${String(nh).padStart(2, "0")}:${String(nm).padStart(2, "0")}`;
}

function generateSessions(startDate, startTime, duration, mode, repeatDays, weeks) {
  if (!startDate) return [];
  const endTime = addHours(startTime, parseInt(duration, 10));

  if (mode === "single" || repeatDays.size === 0) {
    return [{ date: startDate, startTime, endTime }];
  }

  const sessions = [];
  const start = parseLocalDate(startDate);
  const totalDays = weeks * 7;

  for (let offset = 0; offset < totalDays; offset++) {
    const candidate = new Date(start);
    candidate.setDate(start.getDate() + offset);
    if (repeatDays.has(candidate.getDay())) {
      sessions.push({ date: toDateStr(candidate), startTime, endTime });
    }
  }

  return sessions;
}

// ── Shared field wrapper ───────────────────────────────────────────────────────

function Field({ label, children, sx }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "7px", ...sx }}>
      <Typography
        sx={{
          fontSize: "11.5px",
          fontWeight: 700,
          letterSpacing: ".07em",
          textTransform: "uppercase",
          color: "var(--color-ink-3)",
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function ScheduleClassDialog({
  open,
  template,
  startDate: initialStartDate,
  existingEvents,
  instructors,
  onConfirm,
  onClose,
}) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimaryDk = theme.palette.primary.dark;

  const sessionCount = template?.sessionCount || 1;

  // ── Field state ───────────────────────────────────────────────────────────────

  const [startDate, setStartDate] = useState(initialStartDate || "");
  const [startTime, setStartTime] = useState("09:00");
  const [duration, setDuration] = useState("8");
  const [location, setLocation] = useState("");
  const [maxSeats, setMaxSeats] = useState(template?.maxStudents || 24);
  const [instructorId, setInstructorId] = useState("");

  // ── Schedule state ────────────────────────────────────────────────────────────

  const [scheduleMode, setScheduleMode] = useState(
    sessionCount > 1 ? "repeat" : "single"
  );

  const defaultWeekday = useMemo(
    () => (initialStartDate ? parseLocalDate(initialStartDate).getDay() : 1),
    [initialStartDate]
  );

  const [repeatDays, setRepeatDays] = useState(() => new Set([defaultWeekday]));
  const [weeks, setWeeks] = useState(Math.max(1, sessionCount));

  // Reset state when dialog reopens with a new template/date
  useEffect(() => {
    if (!open) return;
    setStartDate(initialStartDate || "");
    setStartTime("09:00");
    setDuration("8");
    setLocation("");
    setMaxSeats(template?.maxStudents || 24);
    setInstructorId("");
    setScheduleMode((template?.sessionCount || 1) > 1 ? "repeat" : "single");
    const wd = initialStartDate ? parseLocalDate(initialStartDate).getDay() : 1;
    setRepeatDays(new Set([wd]));
    setWeeks(Math.max(1, template?.sessionCount || 1));
    // intentionally runs only when `open` flips — resets all form state
    // eslint-disable-next-line
  }, [open]);

  // ── Session generation ────────────────────────────────────────────────────────

  const rawSessions = useMemo(
    () =>
      generateSessions(startDate, startTime, duration, scheduleMode, repeatDays, weeks),
    [startDate, startTime, duration, scheduleMode, repeatDays, weeks]
  );

  const sessionsWithConflicts = useMemo(() => {
    if (!existingEvents?.length) {
      return rawSessions.map((s) => ({ ...s, conflict: null }));
    }
    return rawSessions.map((session) => {
      const conflict = existingEvents.find(
        (ev) =>
          ev.date === session.date &&
          ev.startTime === session.startTime &&
          ((instructorId && String(ev.instructor) === String(instructorId)) ||
            (location.trim() && ev.room === location.trim()))
      );
      return { ...session, conflict: conflict || null };
    });
  }, [rawSessions, existingEvents, instructorId, location]);

  const conflictCount = sessionsWithConflicts.filter((s) => s.conflict).length;
  const generatedCount = sessionsWithConflicts.length;

  // ── Derived ───────────────────────────────────────────────────────────────────

  const fillBadge = useMemo(() => {
    if (generatedCount === sessionCount)
      return {
        label: `Fills all ${sessionCount}`,
        color: "#15803D",
        bg: "rgba(21,128,61,.12)",
      };
    if (generatedCount < sessionCount)
      return {
        label: `${generatedCount} of ${sessionCount} · short ${sessionCount - generatedCount}`,
        color: "#B5651D",
        bg: "rgba(181,101,29,.12)",
      };
    return {
      label: `${generatedCount - sessionCount} over template`,
      color: "var(--color-primary)",
      bg: "rgba(37,99,235,.12)",
    };
  }, [generatedCount, sessionCount]);

  const dateRangeLabel = useMemo(() => {
    if (!sessionsWithConflicts.length) return "";
    const first = sessionsWithConflicts[0].date;
    const last = sessionsWithConflicts[sessionsWithConflicts.length - 1].date;
    return first === last
      ? formatShort(first)
      : `${formatShort(first)} – ${formatShort(last)}`;
  }, [sessionsWithConflicts]);

  // ── Category style ────────────────────────────────────────────────────────────

  const catKey = (template?.category || "").toLowerCase();
  const catColor = CATEGORY_COLOR[catKey] || tPrimary;
  const catBg = CATEGORY_BG[catKey] || alpha(tPrimary, 0.08);

  // ── Handlers ──────────────────────────────────────────────────────────────────

  const toggleDay = (day) => {
    setRepeatDays((prev) => {
      const next = new Set(prev);
      if (next.has(day) && next.size === 1) return next; // keep ≥1 selected
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });
  };

  const fitToRequired = () => {
    setWeeks(Math.max(1, Math.ceil(sessionCount / Math.max(1, repeatDays.size))));
  };

  const applyQuickPattern = (pattern) => {
    setRepeatDays(new Set(pattern.days));
    setWeeks(pattern.weeks);
    setScheduleMode("repeat");
  };

  const handleConfirm = () => {
    onConfirm({
      template,
      startTime,
      duration: parseInt(duration, 10),
      location: location.trim(),
      maxSeats,
      instructorId: instructorId || null,
      sessions: sessionsWithConflicts,
      hasConflicts: conflictCount > 0,
    });
  };

  // ── Shared sx ─────────────────────────────────────────────────────────────────

  const TF_SX = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "11px",
      background: "var(--color-surface-2)",
      fontSize: "14px",
      "& fieldset": { borderColor: "var(--color-hairline-cloud)" },
      "&:hover fieldset": { borderColor: "var(--color-hairline-cool)" },
      "&.Mui-focused fieldset": { borderColor: tPrimary },
    },
  };

  const SEL_SX = {
    borderRadius: "11px",
    background: "var(--color-surface-2)",
    fontSize: "14px",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--color-hairline-cloud)" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--color-hairline-cool)" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: tPrimary },
  };

  if (!template) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          overflow: "hidden",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: "13px",
          p: "22px 24px 18px",
          borderBottom: "1px solid var(--color-surface-press-light)",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "12px",
            background: tPrimary,
            color: "#fff",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <CalendarMonthIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: "18px", fontWeight: 800, letterSpacing: "-.02em", fontFamily: "var(--font-display)" }}>
            Schedule Class
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "var(--color-ink-3)", mt: "3px" }}>
            {sessionCount > 1
              ? `Template has ${sessionCount} sessions — schedule them in bulk.`
              : "Set the details for this class session."}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ color: "var(--color-ink-4)", mt: "-2px", "&:hover": { color: "var(--color-ink)" } }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* ── Scrollable body ─────────────────────────────────────────────────── */}
      <Box sx={{ overflowY: "auto", flex: 1 }}>
        <Box
          sx={{
            p: "20px 24px",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          {/* Template recap */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "11px",
              p: "12px 14px",
              borderRadius: "12px",
              border: "1.5px solid var(--color-hairline-cloud)",
              background: "var(--color-surface-2)",
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "9px",
                background: catBg,
                color: catColor,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                fontWeight: 900,
                fontSize: "14px",
                letterSpacing: "-.01em",
              }}
            >
              {(template.category || "C").charAt(0).toUpperCase()}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{ fontSize: "14px", fontWeight: 700, color: "var(--color-ink)" }}
                noWrap
              >
                {template.title}
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "var(--color-ink-3)" }}>
                {[
                  template.code,
                  template.totalHours && `${template.totalHours} hrs`,
                  sessionCount > 1 && `${sessionCount} sessions`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            </Box>
          </Box>

          {/* Session count callout */}
          {sessionCount > 1 && (
            <Alert
              severity="info"
              icon={false}
              sx={{ borderRadius: "11px", fontSize: "13px", py: "9px" }}
            >
              This class defines <strong>{sessionCount} sessions</strong> of
              instruction.
            </Alert>
          )}

          {/* Start Date — only shown when not pre-set by a calendar drop */}
          {!initialStartDate && (
            <Field label="Start Date">
              <TextField
                type="date"
                fullWidth
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={TF_SX}
              />
            </Field>
          )}

          {/* Start Time + Duration */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
            <Field label="Start Time">
              <TextField
                type="time"
                fullWidth
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={TF_SX}
              />
            </Field>
            <Field label="Duration">
              <Select
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                fullWidth
                sx={SEL_SX}
              >
                {DURATION_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </Field>
          </Box>

          {/* Location + Max Seats */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
            <Field label="Location / Room">
              <TextField
                fullWidth
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g., Bay 3"
                sx={TF_SX}
              />
            </Field>
            <Field label="Max Seats">
              <TextField
                type="number"
                fullWidth
                value={maxSeats}
                onChange={(e) => setMaxSeats(parseInt(e.target.value, 10) || 1)}
                inputProps={{ min: 1, max: 200 }}
                sx={TF_SX}
              />
            </Field>
          </Box>

          {/* Lead Instructor */}
          <Field label="Lead Instructor">
            <Select
              value={instructorId}
              onChange={(e) => setInstructorId(e.target.value)}
              displayEmpty
              fullWidth
              sx={SEL_SX}
            >
              <MenuItem value="">
                <em>Assign later</em>
              </MenuItem>
              {instructors?.map((inst) => (
                <MenuItem key={inst.id} value={inst.id}>
                  {inst.firstName && inst.lastName
                    ? `${inst.firstName} ${inst.lastName}`
                    : inst.username}
                </MenuItem>
              ))}
            </Select>
          </Field>

          {/* SESSION SCHEDULE divider */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "11px" }}>
            <Divider sx={{ flex: 1 }} />
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 800,
                letterSpacing: ".07em",
                textTransform: "uppercase",
                color: "var(--color-ink-4)",
              }}
            >
              Session Schedule
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>

          {/* Single / Repeat toggle */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              background: "var(--color-surface-2)",
              borderRadius: "13px",
              border: "1.5px solid var(--color-hairline-cloud)",
              p: "4px",
              gap: "4px",
            }}
          >
            {[
              {
                mode: "single",
                icon: <EventIcon sx={{ fontSize: 15 }} />,
                label: "Single session",
              },
              {
                mode: "repeat",
                icon: <RepeatIcon sx={{ fontSize: 15 }} />,
                label: "Repeat weekly",
              },
            ].map(({ mode, icon, label }) => (
              <Button
                key={mode}
                onClick={() => setScheduleMode(mode)}
                disableRipple
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: "13px",
                  fontWeight: 700,
                  py: "8px",
                  gap: "7px",
                  background: scheduleMode === mode ? "#fff" : "transparent",
                  color: scheduleMode === mode ? tPrimary : "var(--color-ink-3)",
                  boxShadow:
                    scheduleMode === mode
                      ? "0 1px 4px rgba(0,0,0,.10)"
                      : "none",
                  "&:hover": {
                    background:
                      scheduleMode === mode ? "#fff" : "rgba(0,0,0,.03)",
                  },
                }}
              >
                {icon}
                {label}
              </Button>
            ))}
          </Box>

          {/* Repeat options */}
          {scheduleMode === "repeat" && (
            <>
              {/* Repeat On weekday chips */}
              <Field label="Repeat On">
                <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                  {WEEKDAYS.map((day, idx) => {
                    const active = repeatDays.has(idx);
                    return (
                      <Box
                        key={day}
                        onClick={() => toggleDay(idx)}
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "12px",
                          display: "grid",
                          placeItems: "center",
                          fontSize: "13px",
                          fontWeight: 700,
                          cursor: "pointer",
                          userSelect: "none",
                          border: active
                            ? `2px solid ${tPrimary}`
                            : "1.5px solid var(--color-hairline-cloud)",
                          background: active ? tPrimary : "var(--color-surface-2)",
                          color: active ? "#fff" : "var(--color-ink-3)",
                          transition: "all .12s",
                          "&:hover": {
                            borderColor: active ? tPrimaryDk : "var(--color-hairline-cool)",
                          },
                        }}
                      >
                        {day}
                      </Box>
                    );
                  })}
                </Box>
              </Field>

              {/* For How Long stepper */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: "7px" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Typography
                    sx={{
                      fontSize: "11.5px",
                      fontWeight: 700,
                      letterSpacing: ".07em",
                      textTransform: "uppercase",
                      color: "var(--color-ink-3)",
                    }}
                  >
                    For How Long
                  </Typography>
                  {sessionCount > 1 && (
                    <Typography sx={{ fontSize: "11.5px", color: "var(--color-ink-4)" }}>
                      — template needs {sessionCount} sessions
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  {/* Stepper */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      border: "1.5px solid var(--color-hairline-cloud)",
                      borderRadius: "11px",
                      background: "var(--color-surface-2)",
                      overflow: "hidden",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => setWeeks((w) => Math.max(1, w - 1))}
                      sx={{
                        borderRadius: 0,
                        width: 36,
                        height: 40,
                        color: "var(--color-ink-3)",
                      }}
                    >
                      <RemoveIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        px: "6px",
                      }}
                    >
                      <Typography
                        sx={{
                          minWidth: 24,
                          textAlign: "center",
                          fontSize: "16px",
                          fontWeight: 800,
                          color: "var(--color-ink)",
                        }}
                      >
                        {weeks}
                      </Typography>
                      <Typography sx={{ fontSize: "13.5px", color: "var(--color-ink-3)" }}>
                        weeks
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => setWeeks((w) => w + 1)}
                      sx={{
                        borderRadius: 0,
                        width: 36,
                        height: 40,
                        color: "var(--color-ink-3)",
                      }}
                    >
                      <AddIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                  {/* Fit to required */}
                  {sessionCount > 1 && (
                    <Button
                      size="small"
                      onClick={fitToRequired}
                      startIcon={<CheckIcon sx={{ fontSize: "12px !important" }} />}
                      sx={{
                        height: 40,
                        px: "13px",
                        borderRadius: "11px",
                        border: "1.5px solid var(--color-hairline-cloud)",
                        background: "var(--color-surface-2)",
                        color: "var(--color-ink-2)",
                        fontSize: "12.5px",
                        fontWeight: 700,
                        textTransform: "none",
                        "&:hover": {
                          background: "#EEF0F8",
                          borderColor: tPrimary,
                          color: tPrimary,
                        },
                      }}
                    >
                      Fit to required
                    </Button>
                  )}
                </Box>
              </Box>

              {/* Quick patterns */}
              <Field label="Quick Patterns">
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "7px" }}>
                  {QUICK_PATTERNS.map((pattern) => (
                    <Box
                      key={pattern.label}
                      onClick={() => applyQuickPattern(pattern)}
                      sx={{
                        px: "12px",
                        py: "7px",
                        border: "1.5px solid var(--color-hairline-cloud)",
                        borderRadius: "10px",
                        background: "var(--color-surface-2)",
                        fontSize: "12.5px",
                        fontWeight: 600,
                        color: "var(--color-ink-2)",
                        cursor: "pointer",
                        userSelect: "none",
                        "&:hover": {
                          background: "#EEF0F8",
                          borderColor: tPrimary,
                          color: tPrimary,
                        },
                      }}
                    >
                      {pattern.label}
                    </Box>
                  ))}
                </Box>
              </Field>
            </>
          )}

          {/* Conflict banner */}
          {conflictCount > 0 && (
            <Alert
              severity="warning"
              icon={<WarningAmberIcon sx={{ fontSize: 18 }} />}
              sx={{ borderRadius: "11px", fontSize: "13px" }}
            >
              <strong>
                {conflictCount} conflict{conflictCount > 1 ? "s" : ""}:
              </strong>{" "}
              {instructorId
                ? `Instructor is already booked at ${startTime} on the highlighted dates.`
                : `Room is already in use on the highlighted dates.`}
              {" "}
              Conflicts warn but won't block scheduling.
            </Alert>
          )}

          {/* Live preview card */}
          {generatedCount > 0 && (
            <Box
              sx={{
                p: "16px",
                borderRadius: "14px",
                border: "1.5px solid var(--color-hairline-cloud)",
                background: "var(--color-surface-2)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: "10px",
                }}
              >
                <Box>
                  <Box
                    sx={{ display: "flex", alignItems: "baseline", gap: "6px" }}
                  >
                    <Typography
                      sx={{
                        fontSize: "36px",
                        fontWeight: 900,
                        color: "var(--color-ink)",
                        lineHeight: 1,
                        fontFamily: "var(--font-display)",
                      }}
                    >
                      {generatedCount}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "15px",
                        fontWeight: 700,
                        color: "var(--color-ink)",
                      }}
                    >
                      sessions will be created
                    </Typography>
                  </Box>
                  <Typography
                    sx={{ fontSize: "13px", color: "var(--color-ink-3)", mt: "3px" }}
                  >
                    {dateRangeLabel}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    px: "10px",
                    py: "5px",
                    borderRadius: "9px",
                    background: fillBadge.bg,
                    color: fillBadge.color,
                    fontSize: "12px",
                    fontWeight: 700,
                    flexShrink: 0,
                    ml: "12px",
                  }}
                >
                  {fillBadge.label}
                </Box>
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                {sessionsWithConflicts.map((s, i) => (
                  <Box
                    key={i}
                    sx={{
                      px: "9px",
                      py: "4px",
                      borderRadius: "7px",
                      fontSize: "12px",
                      fontWeight: 600,
                      border: "1.5px solid",
                      borderColor: s.conflict ? "#C2410C" : "var(--color-hairline-cloud)",
                      background: s.conflict
                        ? "rgba(194,65,12,.08)"
                        : "#fff",
                      color: s.conflict ? "#C2410C" : "var(--color-ink-2)",
                    }}
                  >
                    {formatShort(s.date)}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <DialogActions
        sx={{
          px: "24px",
          py: "16px",
          background: "var(--color-surface-2)",
          borderTop: "1px solid var(--color-surface-press-light)",
          gap: "9px",
          flexShrink: 0,
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            height: 38,
            px: "14px",
            borderRadius: "9px",
            border: "1.5px solid var(--color-hairline-cloud)",
            color: "var(--color-ink-3)",
            fontSize: "13px",
            fontWeight: 700,
            textTransform: "none",
            "&:hover": { background: "var(--color-surface-press-light)" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={!startDate}
          startIcon={<CheckIcon sx={{ fontSize: "14px !important" }} />}
          sx={{
            height: 38,
            px: "16px",
            borderRadius: "9px",
            background: tPrimary,
            color: "#fff",
            fontSize: "13px",
            fontWeight: 700,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { background: tPrimaryDk, boxShadow: "none" },
            "&.Mui-disabled": {
              opacity: 0.5,
              color: "#fff",
              background: tPrimary,
            },
          }}
        >
          Schedule Class
        </Button>
      </DialogActions>
    </Dialog>
  );
}
